import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer"
import React from "react"
import theme from "css/theme/light"
import { t } from "i18next"
import moment from "moment"
import { IFormAreaProps } from "../FormArea/formarea.interface"
import { IFormUploadAttachmentProps } from "../FormComponents/FormUploadFile/formuploadfile.interface"
import { IFormFieldProps } from "../FormField/formfield.interface"
import { IFormGroupProps } from "../FormGroup/formgroup.interface"
import { IFormResult } from "./formtemplate.interface"
import { FormFieldComponentEnum } from "pages/customer/pages/settings/pages/users/pages/edit/UserCustomField.interfaces"
import { IApiResult } from "auth/interface/api.interface"
import { FormTemplateKey } from "helpers/FormTemplate/FormTemplateKey"

Font.register({
  family: "area-normal",
  fonts: [
    {
      src: "assets/learning/AreaNormal-Regular.otf"
    },
    {
      src: "assets/learning/AreaNormal-Bold.otf",
      fontWeight: "bold"
    },
    {
      src: "assets/learning/AreaNormal-Light.otf",
      fontWeight: "light"
    }
  ]
})

const styles = StyleSheet.create({
  sectionImage: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    borderColor: theme.palette.grey[400]
  },
  image: {
    width: "48%",
    height: 140,
    borderRadius: 6,
    objectFit: "cover",
    marginRight: 10,
    marginBottom: 10
  },
  loopedItem: {
    minWidth: "50%"
  },
  page: {
    backgroundColor: (theme.palette as any).foreground.inverted,
    fontFamily: "area-normal",
    paddingVertical: 40,
    paddingHorizontal: 40
  },
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    breakInside: "avoid",
    break: "auto"
  },
  headerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between"
  },
  logoContainer: {
    width: 100,
    height: 40,
    justifyContent: "center",
    alignItems: "flex-end"
  },

  logoImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain"
  },

  docTitle: {
    fontFamily: "area-normal",
    fontSize: 20,
    fontWeight: "bold",
    color: theme.palette.grey[700],
    letterSpacing: 1.5,
    textAlign: "left"
  },
  headerDivider: {
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.grey[700],
    width: "100%",
    marginBottom: 12
  },
  sectionBlock: {
    breakInside: "avoid",
    break: "auto"
  },
  sectionHeader: {
    fontFamily: "area-normal",
    fontSize: 13,
    fontWeight: "bold",
    color: theme.palette.grey[700],
    paddingBottom: 12
  },
  sectionContent: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    borderWidth: 1,
    borderColor: theme.palette.grey[400],
    borderRadius: 6,
    paddingTop: 12,
    paddingHorizontal: 12
  },
  sectionAdminQuestions: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    borderWidth: 1,
    borderColor: theme.palette.grey[400],
    borderRadius: 6,
    paddingTop: 12,
    paddingBottom: 12,
    paddingHorizontal: 12
  },
  paddingBottom8: {
    paddingBottom: 12
  },
  fieldWrapper: {
    marginBottom: 12
  },
  label: {
    fontFamily: "area-normal",
    fontSize: 10,
    color: theme.palette.grey[400],
    marginBottom: 6
  },
  bodyText: {
    fontFamily: "area-normal",
    fontSize: 11,
    color: theme.palette.grey[900]
  },
  signatureSection: {
    paddingTop: 16,
    breakInside: "avoid"
  },
  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%"
  },
  signatureLabel: {
    fontSize: 11,
    color: theme.palette.grey[600]
  },
  signatureBox: {
    height: 30,
    borderWidth: 1,
    borderColor: theme.palette.grey[400],
    borderRadius: 6,
    justifyContent: "center",
    paddingHorizontal: 12
  },
  signaturePlaceholder: {
    fontSize: 10,
    color: theme.palette.grey[400]
  },
  signatureMetaRow: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  signatureMetaText: {
    fontSize: 9,
    color: theme.palette.grey[500]
  },
  signatureFieldSpacer: {
    marginTop: 16
  },
  signatureLineBox: {
    height: 38,
    justifyContent: "flex-end",
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.grey[400]
  },
  signatureLineHint: {
    fontSize: 9,
    color: theme.palette.grey[400],
    textAlign: "right"
  },
  pageFlex: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  }
})

const hasAdminRole = (area: IFormAreaProps) => {
  if (!area.roles) return false

  const roles = area.roles.map((role) => role.trim())
  return roles.some((role) => role.endsWith("Admin"))
}

interface PdfFormTemplateProps extends IFormResult {
  deviationResult: IApiResult
}

export const PdfFormTemplate: React.FC<PdfFormTemplateProps> = ({ allDeviationData, logoResult, deviationResult, ...formResult }) => {
  const getUploadFieldValues = (formResult: IFormResult): IFormUploadAttachmentProps[] => {
    if (!formResult?.formAreas) return []
    const isDeviationGotlandSchoolBus = allDeviationData?.key === "gotland_school_bus"

    return formResult.formAreas
      ?.filter((area: IFormAreaProps) => !(hasAdminRole(area) && !isDeviationGotlandSchoolBus))
      .flatMap(
        (area: IFormAreaProps) =>
          area.formGroups?.flatMap((group: IFormGroupProps) =>
            group.formFields
              ?.filter((field: IFormFieldProps) => field.component === "Upload" && field.value)
              .flatMap((field: IFormFieldProps) => {
                const { value } = field

                if (Array.isArray(value)) return value

                try {
                  const parsed = JSON.parse(value as any)
                  return Array.isArray(parsed) ? parsed : []
                } catch {
                  return []
                }
              })
          ) || []
      )
      .filter((file: any) => file?.Data)
  }

  const isDeviationGotlandSchoolBus = allDeviationData?.key === "gotland_school_bus"
  const uploadFields = getUploadFieldValues({ ...formResult })
  const formTemplateKey = deviationResult.data?.formResult?.formTemplate?.key

  const logoSrc =
    logoResult?.base64Contents && logoResult?.contenType
      ? `data:${logoResult.contenType};base64,${logoResult.base64Contents}`
      : `${window.location.origin}/assets/route-by-ecomobility.png`

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={[styles.pageContainer, styles.pageFlex]}>
          <View style={styles.headerContainer}>
            <Text style={styles.docTitle}>{formResult.name}</Text>
            <View style={styles.logoContainer}>
              <Image style={styles.logoImage} src={logoSrc} />
            </View>
          </View>
          <View style={styles.headerDivider} />
          {/* ---------- DYNAMIC SECTIONS ---------- */}
          {formResult.formAreas
            ?.filter((area: IFormAreaProps) => !(hasAdminRole(area) && !isDeviationGotlandSchoolBus))
            .map((area: IFormAreaProps) => {
              const formGroups = area.formGroups ?? []
              const formFieldsLengths = formGroups.map((group) => group.formFields?.length ?? 0)
              const findFormFieldsLongerThanTen = formFieldsLengths.find((length) => length >= 10)
              const hasAnsweredAdminFields = formGroups.some((group) =>
                group.formFields?.some((field) => field.value != null)
              )
              const userType = hasAdminRole(area) ? t("global.boss", "Manager") : t("global.driver", "Driver")
              const signatureText = `${t("global.signature2", "Signature")} ${userType?.toLowerCase()}`

              return (
                hasAnsweredAdminFields && (
                  <View break={!!findFormFieldsLongerThanTen} key={area.id} style={styles.sectionBlock}>
                    {/* Section content (fields) */}
                    <View style={styles.sectionContent}>
                      {area?.formGroups?.map((formGroup: IFormGroupProps) =>
                        formGroup.formFields.map((field: IFormFieldProps, fieldIndex: number) => {
                          const dependencyIds = (() => {
                            if (!field.dependencyIds) return []
                            try {
                              const parsed = JSON.parse(field.dependencyIds.toString())
                              return Array.isArray(parsed) ? parsed : []
                            } catch {
                              return []
                            }
                          })()

                          const shouldShow =
                            dependencyIds.length === 0 ||
                            area?.formGroups?.some((fg: IFormGroupProps) =>
                              fg.formFields.some((ff: IFormFieldProps) =>
                                dependencyIds.some(
                                  (d: any) => d.formFieldKey === ff.formFieldKey && ff.value === d.value
                                )
                              )
                            )

                          const nextField = formGroup.formFields[fieldIndex]
                          const showDriverBlock = nextField?.labelKey === "vehicle_damage_S4_FG1_FF6"
                            && formTemplateKey === FormTemplateKey.VehicleDamage

                          return (
                            shouldShow && (
                              <>
                                {showDriverBlock && (
                                  <View style={styles.fieldWrapper}>
                                    <View style={styles.loopedItem}>
                                      <Text style={styles.label}>{t("global.driver", "Driver")}</Text>
                                      <Text style={styles.bodyText}>{deviationResult?.data?.involvedUserName || ""}</Text>
                                    </View>
                                  </View>
                                )}
                                <View key={field.id} style={styles.fieldWrapper}>
                                  <PdfFormComponents {...field} />
                                </View>
                              </>
                            )
                          )
                        })
                      )}
                    </View>
                    {isDeviationGotlandSchoolBus && (
                      <View style={styles.signatureSection}>
                        <View style={styles.signatureContainer}>
                          <Text style={styles.bodyText}>{signatureText}</Text>
                          <Text style={styles.bodyText}>
                            {t("global.name_clarification", "Name clarification")}
                          </Text>
                        </View>
                        <View style={styles.signatureLineBox}></View>
                      </View>
                    )}
                  </View>
                )
              )
            })}
        </View>
        {/* ---------- SIGNATURE SECTION ---------- */}
        {/* {isDeviationGotlandSchoolBus &&
          <View style={styles.signatureSection}>
            <View style={styles.signatureContainer}>
              <Text style={styles.bodyText}>
                {t("global.signature", "Signature")}
              </Text>
              <Text style={styles.bodyText}>
                {t("global.name_clarification", "Name clarification")}
              </Text>
            </View>
            <View style={styles.signatureLineBox}></View>
          </View>} */}
      </Page>

      {uploadFields.length > 0 ? (
        <Page size="A4" style={styles.page}>
          <View style={styles.pageContainer}>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionHeader}>{t("deviation.uploadedFiles", "Uploaded Files")}</Text>
              <View style={styles.sectionImage}>
                {uploadFields?.map((file, index) => (
                  <PdfImage key={index} attachment={file} />
                ))}
              </View>
            </View>
          </View>
        </Page>
      ) : null}
    </Document>
  )
}

function isJsonString(str: string) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

export const PdfImage: React.FC<{ attachment: IFormUploadAttachmentProps | any }> = ({ attachment }) => {
  if (!attachment) return null
  const mimeType = attachment.Data.startsWith("/9j/") ? "image/jpeg" : "image/png"

  return <Image style={styles.image} src={`data:${mimeType};base64,${attachment.Data}`} />
}

const PdfFormComponents: React.FC<IFormFieldProps> = (props) => {
  const { labelKey, labelDefault, value, component, options } = props
  const textLabel =
    labelKey !== null || labelDefault !== null ? t(`deviation.${labelKey}`, labelDefault) || labelDefault : ""

  switch (component) {
    case "CardButton": {
      const optionsTextLabel = options?.find((x) => x.value?.toString() === value)
      const translatedOptionsTextLabel = optionsTextLabel
        ? t(`deviation.${optionsTextLabel.labelKey}`, optionsTextLabel.labelDefault)
        : ""
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{translatedOptionsTextLabel}</Text>
        </View>
      )
    }

    case "RadioButtons":
    case "BigRadioButtons": {
      const optionsTextLabel = options?.find((x) => x.value?.toString() === value)
      const translatedOptionsTextLabel = optionsTextLabel
        ? t(`deviation.${optionsTextLabel.labelKey}`, optionsTextLabel.labelDefault)
        : ""
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{translatedOptionsTextLabel}</Text>
        </View>
      )
    }

    case "Checkbox":
    case FormFieldComponentEnum.Switch: {
      return value === "true" ? (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>
            {value === "true" ? t("global.yes", "Yes") : t("global.no", "No")}
          </Text>
        </View>
      ) : null
    }

    case "DateTimePicker": {
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{moment(value).format("YYYY-MM-DD HH:mm")}</Text>
        </View>
      )
    }

    case "DatePicker": {
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{moment(value).format("YYYY-MM-DD HH:mm")}</Text>
        </View>
      )
    }

    case "DateTime": {
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{moment(value).format("YYYY-MM-DD")}</Text>
        </View>
      )
    }
    case "LocationPicker": {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null

      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{convertedValue?.interpretedValue}</Text>
        </View>
      )
    }

    case "VehiclePicker": {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null

      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{convertedValue?.RegNumber}</Text>
        </View>
      )
    }
    case "Select": {
      const optionsTextLabel = options?.find((x) => x.value?.toString() === value)
      const translatedOptionsTextLabel = optionsTextLabel
        ? t(`deviation.${optionsTextLabel.labelKey}`, optionsTextLabel.labelDefault)
        : ""

      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{translatedOptionsTextLabel}</Text>
        </View>
      )
    }
    case "Upload":
      return null

    case "BPLStationPicker":
    case "DepartmentPicker": {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{convertedValue?.Name || value}</Text>
        </View>
      )
    }

    case FormFieldComponentEnum.CompanyPicker:
    case FormFieldComponentEnum.UserPicker: {
      const convertedValue = value && isJsonString(value) ? JSON.parse(value) : null

      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{convertedValue?.Name || value}</Text>
        </View>
      )
    }

    default:
      return (
        <View style={styles.loopedItem}>
          <Text style={styles.label}>{t(textLabel)}</Text>
          <Text style={styles.bodyText}>{value}</Text>
        </View>
      )
  }
}
